import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Trash2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/common/Common";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDateTime, formatError } from "@/lib/api";

const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export function DocumentsPanel({ contractId, personId }) {
  const { canWrite, isAdmin } = useAuth();
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const params = contractId ? { contract_id: contractId } : { person_id: personId };
  const load = useCallback(() => api.get("/documents", { params }).then((r) => setDocs(r.data)), [contractId, personId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const upload = async (files) => {
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      if (contractId) fd.append("contract_id", contractId);
      else fd.append("person_id", personId);
      try { await api.post("/documents", fd); toast.success(`Uploaded ${f.name}`); } catch (e) { toast.error(`${f.name}: ${formatError(e)}`); }
    }
    setBusy(false);
    input.current.value = "";
    load();
  };
  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: d.original_filename });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (d) => {
    try { await api.delete(`/documents/${d.id}`); toast.success("Document removed"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="documents-panel">
      {canWrite && (
        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center bg-white"
          onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload([...e.dataTransfer.files]); }}>
          <Upload className="h-6 w-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-600">Drag files here or</p>
          <input ref={input} type="file" multiple className="hidden" onChange={(e) => upload([...e.target.files])} data-testid="document-file-input" />
          <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={() => input.current.click()} data-testid="document-upload-button">
            {busy && <Spinner className="mr-2" />}Choose files
          </Button>
          <p className="text-[11px] text-slate-400 mt-2">Private storage · PDF, Word, Excel, images · max 20 MB</p>
        </div>
      )}
      <ul className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {docs.map((d) => (
          <li key={d.id} className="flex items-center gap-3 px-4 py-3" data-testid={`document-row-${d.id}`}>
            <FileText className="h-4 w-4 text-blue-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-slate-800 truncate">{d.original_filename}</p>
              <p className="text-[11px] text-slate-400">{kb(d.size)} · {d.uploaded_by} · {fmtDateTime(d.created_at)}</p>
            </div>
            <Button size="icon" variant="ghost" onClick={() => download(d)} data-testid={`document-download-${d.id}`}><Download className="h-4 w-4" /></Button>
            {isAdmin && <Button size="icon" variant="ghost" onClick={() => remove(d)} data-testid={`document-delete-${d.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
          </li>
        ))}
        {!docs.length && <li className="py-6 text-center text-sm text-slate-400">No documents yet</li>}
      </ul>
    </div>
  );
}
